// @flow
import React from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { get, map, isEmpty } from 'lodash';
import { router } from '@router';
import { redirectToDashboard } from '@context/user';
import { Dropdown, Icon } from '@elements';
// import { shortenName } from '@helpers/helpers';
import type { User, ClinicsState, BundledPermissions } from '@types';
import { createNsTranslator, isBetaApp, openSupportChat, fsPrintf } from '@helpers/helpers';
import { withTranslation } from 'react-i18next';
import { blockedLinkTo } from '../../../routes/blockedAccess';
import './AppHeader.scss';

type Props = {
  user: ?User,
  accessToken: string,
  clinics: ?ClinicsState,
  onLogout: Function,
  onUpdateApp: Function,
  onChangeClinic: Function,
  isIntercomEnabled: boolean,
  trial: boolean,
  trialDays: number,
  t: Function,
  permissions: BundledPermissions,
  // An iOS build in App Store review: the trial badge doesn't lead to the plans, no dashboard
  storeReview?: boolean,
};

const helpList = (intercom: boolean, _: Function, dashboard: boolean, userId: number, clinicId: number) => [
  ...(dashboard ? [{ text: _('label/dashboard'), action: 'navigateToDashboard', to: `/users/${userId}/clinics/${clinicId}` }] : []),
  ...(intercom ? [{ text: _('label/online_support'), action: 'onlineSupport' }] : []),
  { text: _('action/reload'), action: 'update' },
];

class AppHeader extends React.Component<Props> {
  render() {
    const {
      user, clinics, isIntercomEnabled,
      trial, trialDays, permissions, storeReview,
    } = this.props;

    const _ = createNsTranslator('layout', this.props.t);

    // Guard against potential null values
    if (!user || !clinics) return false;

    const advancedData = get(user, 'advancedData', {});

    const clinicNames = [];
    let activeClinicName;
    if (!clinics.isLoading) {
      const multiClinics = get(clinics, 'data', {});

      if (!isEmpty(multiClinics)) {
        activeClinicName = get(clinics, ['activeClinic', 'name'], '');
        clinicNames.push({
          break: true, text: 'Mudar de Clínica', className: 'headingSeparator',
        });
        map(multiClinics, (clinic) => {
          clinicNames.push({
            text: clinic.name,
            clinicId: get(clinic, 'id'),
            action: 'selectClinic',
          });
        });
      }
    }

    let settingLinkTo = !permissions.isDependent ? '/ajustes/meu-plano' : '/ajustes/meu-perfil';

    switch (advancedData.subscriptionStatus) {
      case 'trialing': settingLinkTo = '/ajustes/meu-perfil'; break;
      case 'end_of_trial': settingLinkTo = '/ajustes/meu-perfil'; break;
      case 'canceled': settingLinkTo = '/ajustes/meu-perfil'; break;
      case 'banned': settingLinkTo = '/ajustes/meu-perfil'; break;
      default: break;
    }

    const menuList = [
      { text: _('label/settings'), linkTo: settingLinkTo },
      ...clinicNames,
      { break: true, className: 'headingSeparator', text: '  ' },
      { text: _('action/logout'), action: 'logout' },
    ];

    const isBeta = isBetaApp();

    // For an account without an active subscription, the closed pages' links go straight to its plan
    // (blockedLinkTo), with a lock
    const account = { blocked: get(advancedData, 'blocked', false), subscriptionStatus: advancedData.subscriptionStatus };
    const navLink = (name: string, text: string) => {
      const { to, closed } = blockedLinkTo(router.getRoutePath(name), account);
      return (
        <Link to={to} className={classnames({ closed })} title={closed ? _('label/closed_tab', null, fsPrintf(text)) : undefined}>
          {closed && <Icon iconName="lock" size={12} fill="#fff" className="closedLock" />}
          {text}
        </Link>
      );
    };

    return (
      <div className="appHeader">
        <div className="left">
          <Link to={blockedLinkTo('/', account).to} className="logo" />
          <nav>
            {navLink('agenda', _('label/agenda'))}
            {navLink('patientsList', _('label/patients'))}
            {navLink('reportsDebt', _('label/reports'))}
          </nav>
        </div>
        <div className="right">
          <nav>
            {trial && !storeReview && <Link to='/planos' className="header-notification" ><Icon iconName="time" fill="#fff" />&nbsp;{trialDays} {_('label/remaining_trial_days')}</Link>}
            {trial && storeReview && <span className="header-notification"><Icon iconName="time" fill="#fff" />&nbsp;{trialDays} {_('label/remaining_trial_days')}</span>}
            {isBeta && <a href="#" className="header-notification" >{_('label/beta_app')}</a>}
            <Dropdown
                // forceDesktop
              mobileLabel={_('label/help')}
              className="headerHelpDropdown"
              icon="help"
              items={helpList(isIntercomEnabled, _, permissions.dashboard && !storeReview, get(user, 'id'), get(clinics, ['activeClinic', 'id']))}
              onSelectItem={this.handleDropdownItemClick}
            />
            <Link to={settingLinkTo}>{_('label/settings')}</Link>
            <Dropdown
                // forceDesktop
              className="headerMenuDropdown"
              // placeholder={shortenName(user.nameWithTitle)}
              placeholder={user.nameWithTitle || ''}
              secondaryText={activeClinicName}
              items={menuList}
              onSelectItem={this.handleDropdownItemClick}
            />
          </nav>
        </div>
      </div>
    );
  }

  handleDropdownItemClick = (item: any) => {
    const { onLogout, onChangeClinic, accessToken } = this.props;

    switch (item.action) {
      case 'logout':
        onLogout();
        if (window.Intercom) {
          window.Intercom('shutdown');
        }
        break;
      case 'selectClinic':
        onChangeClinic(item.clinicId);
        break;
      case 'onlineSupport':
        openSupportChat();
        break;
      case 'update':
        this.props.onUpdateApp();
        break;
      case 'navigateToDashboard':
        redirectToDashboard(item.to, accessToken);
        break;
      default:
        break;
    }
  }
}

export default withTranslation()(AppHeader);
